import { z } from 'zod';
import { isoDateSchema, optional, versionSchema } from './common';

/*
 * Sessão "Importação em Andamento" (docs/09-importacao-e-perfis.md): embarques de mercadoria importada.
 * Não confundir com `imports.ts`, que é a importação de planilhas do PCP (`es-pcp-import`).
 * Tabelas Progress: es-imp-embarque, es-imp-item, es-imp-fatura.
 */

/** 1 ORDER · 2 IN TRANSIT · 3 CLEARANCE · 4 IN HOUSE · 5 PORT · 6 CANCELED · 7 ENTREPOSTO */
export const SITUACAO_EMBARQUE = {
  ORDER: 1,
  IN_TRANSIT: 2,
  CLEARANCE: 3,
  IN_HOUSE: 4,
  PORT: 5,
  CANCELED: 6,
  ENTREPOSTO: 7,
} as const;

export const situacaoEmbarqueSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);
export type SituacaoEmbarque = z.infer<typeof situacaoEmbarqueSchema>;

/** Situações do histórico (docs/09 seção 8): fechado ou cancelado. */
export const SITUACOES_HISTORICO: readonly SituacaoEmbarque[] = [4, 6];
export const SITUACOES_ANDAMENTO: readonly SituacaoEmbarque[] = [1, 2, 3, 5, 7];
/** Padrão de "em trânsito" (configurável no BFF por `IMP_SITUACOES_TRANSITO`). */
export const SITUACOES_TRANSITO_PADRAO: readonly SituacaoEmbarque[] = [2, 3, 5, 7];

export const MODAIS = ['MARITIMO', 'AEREO', 'RODOVIARIO'] as const;
export const modalSchema = z.enum(MODAIS);

// ---------- es-imp-item ----------

const itemEmbarqueBase = z.object({
  seq: z.number().int().positive(),
  /** Sempre texto: há códigos numéricos de 8 dígitos e alfanuméricos com barra e hífen. */
  'it-codigo': z.string().min(1),
  'num-pedido': optional(z.number().int()),
  'numero-ordem': optional(z.number().int()),
  quantidade: z.number().positive(),
  un: z.string().min(1),
  preco: optional(z.number().nonnegative()),
  po: optional(z.string()),
  rc: optional(z.string()),
  /** `true` só em linha migrada da planilha sem `OC`. */
  'sem-ordem': z.boolean().default(false),
});
export const itemEmbarqueSchema = itemEmbarqueBase.extend({ version: versionSchema });
export type ItemEmbarque = z.infer<typeof itemEmbarqueSchema>;
/** Item enviado numa escrita: `version` só existe em item já gravado; `seq` é atribuído quando ausente. */
export const itemEmbarqueEnvioSchema = itemEmbarqueBase.extend({
  seq: z.number().int().positive().optional(),
  version: versionSchema.optional(),
});
export type ItemEmbarqueEnvio = z.infer<typeof itemEmbarqueEnvioSchema>;

// ---------- es-imp-fatura (financeiro) ----------

const faturaBase = z.object({
  'nr-invoice': z.string().min(1),
  'dt-invoice': optional(isoDateSchema),
  'valor-invoice': optional(z.number()),
  moeda: optional(z.string()),
  'valor-brl': optional(z.number()),
  'dt-venc-invoice': optional(isoDateSchema),
  'cond-pagto': optional(z.string()),
  'custo-desemb-brl': optional(z.number()),
});
export const faturaEmbarqueSchema = faturaBase.extend({ version: versionSchema });
export type FaturaEmbarque = z.infer<typeof faturaEmbarqueSchema>;
export const faturaEmbarqueEnvioSchema = faturaBase.extend({ version: versionSchema.optional() });
export type FaturaEmbarqueEnvio = z.infer<typeof faturaEmbarqueEnvioSchema>;

// ---------- es-imp-embarque ----------

/** Campos de cabeçalho editáveis (tudo menos `nr-import`, `situacao` e `version`). */
const cabecalhoCampos = {
  exportador: optional(z.string()),
  incoterm: optional(z.string()),
  origem: optional(z.string()),
  modal: optional(modalSchema),
  despachante: optional(z.string()),
  terminal: optional(z.string()),
  'bl-awb': optional(z.string()),
  'dt-bl-awb': optional(isoDateSchema),
  'dt-etd-prev': optional(isoDateSchema),
  'dt-etd-real': optional(isoDateSchema),
  'dt-eta-prev': optional(isoDateSchema),
  'dt-eta-real': optional(isoDateSchema),
  'dt-desemb-prev': optional(isoDateSchema),
  'dt-desemb-real': optional(isoDateSchema),
  'dt-entrega-prev': optional(isoDateSchema),
  'dt-entrega-real': optional(isoDateSchema),
  'nr-nf': optional(z.string()),
  'dt-nf': optional(isoDateSchema),
  drawback: optional(z.string()),
  'dt-armazenagem-venc': optional(isoDateSchema),
  obs: optional(z.string().max(1000)),
};

export const embarqueSchema = z.object({
  /** `NNN/AA` com sufixo opcional `-X` (ex. `054/26`, `071/26-B`). */
  'nr-import': z.string().min(1),
  situacao: situacaoEmbarqueSchema,
  ...cabecalhoCampos,
  version: versionSchema,
});
export type Embarque = z.infer<typeof embarqueSchema>;

/** Calculado pela API, não gravado: data prevista vencida e ainda em trânsito. */
const derivados = { atrasado: z.boolean() };

export const embarqueLogisticaSchema = embarqueSchema.extend({
  itens: z.array(itemEmbarqueSchema),
  ...derivados,
});
export type EmbarqueLogistica = z.infer<typeof embarqueLogisticaSchema>;

export const embarqueCompletoSchema = embarqueLogisticaSchema.extend({
  faturas: z.array(faturaEmbarqueSchema),
});
export type EmbarqueCompleto = z.infer<typeof embarqueCompletoSchema>;
/** Embarque como o Datasul o guarda: sem os campos que a API calcula (`atrasado`). */
export type EmbarqueBruto = Omit<EmbarqueCompleto, 'atrasado'>;

/** O serializador do BFF devolve um ou outro conforme o perfil (`faturas` some para quem não pode ver). */
export type EmbarqueResposta = EmbarqueLogistica & { faturas?: FaturaEmbarque[] };

export const embarquesQuerySchema = z.object({
  situacao: z
    .string()
    .regex(/^[1-7](,[1-7])*$/, 'Use situações de 1 a 7 separadas por vírgula')
    .optional(),
  /** `andamento` (1, 2, 3, 5, 7) ou `historico` (4, 6); ignorado se `situacao` vier. */
  grupo: z.enum(['andamento', 'historico']).optional(),
  item: z.string().optional(),
  'nr-import': z.string().optional(),
  de: isoDateSchema.optional(),
  ate: isoDateSchema.optional(),
  atrasado: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type EmbarquesQuery = z.infer<typeof embarquesQuerySchema>;

// ---------- Escrita ----------

const cabecalhoNovoSchema = z.object({
  'nr-import': z.string().min(1),
  situacao: situacaoEmbarqueSchema.default(1),
  ...cabecalhoCampos,
});

export const embarqueNovoSchema = z.object({
  embarque: cabecalhoNovoSchema,
  itens: z.array(itemEmbarqueEnvioSchema).min(1, 'Informe ao menos um item'),
  faturas: z.array(faturaEmbarqueEnvioSchema).default([]),
  /** Confirma os avisos que pedem confirmação (ex. `nr-import` fora do padrão). */
  confirmar: z.boolean().default(false),
});
export type EmbarqueNovo = z.infer<typeof embarqueNovoSchema>;

export const embarqueAtualizacaoSchema = z.object({
  version: versionSchema,
  embarque: z
    .object({ situacao: situacaoEmbarqueSchema.optional(), ...cabecalhoCampos })
    .partial()
    .default({}),
  /** Estado final dos itens: com `version` atualiza; sem `version` cria; o que faltar é mantido. */
  itens: z.array(itemEmbarqueEnvioSchema).optional(),
  faturas: z.array(faturaEmbarqueEnvioSchema).optional(),
  confirmar: z.boolean().default(false),
});
export type EmbarqueAtualizacao = z.infer<typeof embarqueAtualizacaoSchema>;

export const avisoSchema = z.object({ campo: z.string().optional(), mensagem: z.string() });
export type Aviso = z.infer<typeof avisoSchema>;

export const resultadoEmbarqueSchema = z.object({
  embarque: embarqueCompletoSchema.partial({ faturas: true }),
  avisos: z.array(avisoSchema),
});
export type ResultadoEmbarque = { embarque: EmbarqueResposta; avisos: Aviso[] };

// ---------- Ordens, trânsito, alertas ----------

export const itemCatalogoSchema = z.object({
  'it-codigo': z.string(),
  descricao: z.string(),
  un: z.string(),
});
export type ItemCatalogo = z.infer<typeof itemCatalogoSchema>;

/** Ordem de compra aberta (`ordem-compra` + `prazo-compra` com saldo). */
export const ordemAbertaSchema = z.object({
  'numero-ordem': z.number().int(),
  'num-pedido': z.number().int(),
  'it-codigo': z.string(),
  descricao: z.string().optional(),
  un: z.string(),
  'quant-saldo': z.number(),
  /** Saldo menos o que já está em embarques abertos (piso zero). */
  'a-embarcar': z.number(),
});
export type OrdemAberta = z.infer<typeof ordemAbertaSchema>;

/** Ordem de compra como vem do Datasul (sem o `a-embarcar`, que o BFF calcula). */
export type OrdemCompra = Omit<OrdemAberta, 'a-embarcar'>;

export const ordensQuerySchema = z.object({ item: z.string().optional() });
export type OrdensQuery = z.infer<typeof ordensQuerySchema>;

export const transitoSchema = z.object({
  'it-codigo': z.string(),
  /** `2026-W42`; nulo quando `sem-data`. */
  'semana-iso': z.string().nullable(),
  quantidade: z.number(),
  un: z.string(),
  atrasado: z.boolean(),
  'sem-data': z.boolean(),
});
export type Transito = z.infer<typeof transitoSchema>;

export const transitoQuerySchema = z.object({
  item: z.string().optional(),
  de: isoDateSchema.optional(),
  ate: isoDateSchema.optional(),
});
export type TransitoQuery = z.infer<typeof transitoQuerySchema>;

export const alertasSchema = z.object({
  atrasados: z.number().int().nonnegative(),
  itens: z.array(
    z.object({
      'nr-import': z.string(),
      'it-codigo': z.string(),
      situacao: situacaoEmbarqueSchema,
      'dt-entrega-prev': isoDateSchema,
      'dias-atraso': z.number().int().positive(),
    }),
  ),
});
export type Alertas = z.infer<typeof alertasSchema>;

// ---------- Carga inicial da planilha ----------

/** Colunas da aba IMPORTAÇÃO 2026, com os cabeçalhos exatos da planilha ("EXPECTED DELIVERY " leva espaço). */
export const COLUNAS_PLANILHA_IMPORTACAO = [
  'IMPORT',
  'STATUS',
  'COD',
  'QTD',
  'MEASURE',
  'PRICE',
  'RC',
  'OC',
  'PO',
  'EXPORTER',
  'INCOTERM',
  'ORIGIN',
  'MODAL',
  'BROKER',
  'TERMINAL',
  'BOOKING/BL/AWB',
  'DATE BL/AWB',
  'ETD',
  'ATD',
  'ETA',
  'ATA',
  'PLANNED CLEARANCE',
  'CLEARANCE',
  'EXPECTED DELIVERY ',
  'DELIVERY',
  'NF ID',
  'NF DATE',
  'STORAGE DUE',
  'DRAWBACK',
  'OBSERVAÇÕES',
  'INVOICE',
  'INVOICE DATE',
  'INVOICE AMOUNT',
  'CURRENCY',
  'INVOICE BRL',
  'INVOICE DUE',
  'PAYMENT CONDITION',
  'CLEARANCE COST - BRL',
] as const;

export const cargaInicialBodySchema = z.object({
  /** Cada linha da planilha como `{ "<cabeçalho>": "<texto>" }`; o web lê o `.xlsx` no navegador. */
  linhas: z.array(z.record(z.string(), z.string())).min(1).max(5000),
});
export type CargaInicialBody = z.infer<typeof cargaInicialBodySchema>;

export const cargaInicialQuerySchema = z.object({
  simular: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
});

export const MOTIVOS_REJEICAO = [
  'ITEM_INEXISTENTE',
  'UNIDADE_DIVERGENTE',
  'PROCESSO_DUPLICADO',
  'DATA_FUTURA_EM_CAMPO_REAL',
  'SITUACAO_INVALIDA',
  'DADO_OBRIGATORIO',
] as const;
export const motivoRejeicaoSchema = z.enum(MOTIVOS_REJEICAO);
export type MotivoRejeicao = z.infer<typeof motivoRejeicaoSchema>;

export const relatorioCargaSchema = z.object({
  simulacao: z.boolean(),
  lidas: z.number().int(),
  embarques: z.number().int(),
  importadas: z.number().int(),
  rejeitadas: z.number().int(),
  /** Quantas linhas caíram em cada motivo. */
  'por-motivo': z.record(z.string(), z.number().int()),
  rejeicoes: z.array(
    z.object({
      'num-linha': z.number().int(),
      'nr-import': z.string().optional(),
      motivo: motivoRejeicaoSchema,
      mensagem: z.string(),
    }),
  ),
  /** Linhas migradas sem vínculo de ordem (`OC` não convertida): ficam com `sem-ordem=true`. */
  'sem-ordem': z.number().int(),
});
export type RelatorioCarga = z.infer<typeof relatorioCargaSchema>;

// ---------- Auditoria ----------

export const ACOES_AUDITORIA = ['CRIAR', 'EDITAR', 'EXPORTAR', 'LOGIN', 'LOGIN_FALHO'] as const;
export const auditoriaCampoSchema = z.object({
  id: z.number().int(),
  'dt-hora': z.string(),
  usuario: z.string().nullable(),
  perfil: z.string().nullable(),
  recurso: z.string(),
  chave: z.string().nullable(),
  acao: z.enum(ACOES_AUDITORIA),
  campo: z.string().nullable(),
  'valor-anterior': z.string().nullable(),
  'valor-novo': z.string().nullable(),
  filtros: z.record(z.string(), z.unknown()).nullable(),
});
export type AuditoriaCampo = z.infer<typeof auditoriaCampoSchema>;

export const auditoriaQuerySchema = z.object({
  recurso: z.string().optional(),
  chave: z.string().optional(),
  usuario: z.string().optional(),
  de: isoDateSchema.optional(),
  ate: isoDateSchema.optional(),
});
export type AuditoriaQuery = z.infer<typeof auditoriaQuerySchema>;
